/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { ImpressaoAtendimentoVacinacaoInput } from 'api/ImpressaoAtendimentoVacinacao'
import {
  Alert,
  Button,
  ButtonGroup,
  Heading,
  HeadingSection,
  HFlow,
  Text,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'
import { useAlert } from 'components/alert'
import { DateTime } from 'components/date'
import { useErrorHandler } from 'components/error'
import useFirebase from 'components/firebase/useFirebase'
import { CheckboxField, Form, FormRenderProps, FormValueSpy, parseDateFromLocalStorage } from 'components/form'
import { PageContent } from 'components/layout/PageContent'
import { confirm } from 'components/modals/confirm'
import { ATENDIMENTO_NOVO_ENABLED, CEO_ENABLED } from 'config/flags'
import { useApolloClient } from 'graphql/hooks'
import {
  useCancelarAtendimentoMutation,
  useFinalizarAtendimentoIndividualMutation,
  useFinalizarAtendimentoVacinacaoMutation,
} from 'graphql/hooks.generated'
import { TipoRegistroVacinacaoEnum } from 'graphql/types.generated'
import { Dispatch, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { sexoByIdentidadeGenero } from 'util/sexoByIdentidadeGenero'
import { createValidator } from 'util/validation'
import { converAtendimentoIndividualModelToInput } from 'view/atendimentos/atendimento-individual/converter'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { convertAtendimentoVacinacaoModelToInput } from 'view/atendimentos/atendimento-vacinacao/graphql/converter'
import {
  AtendimentoVacinacaoState,
  meta,
  RegistroVacinacaoFormModel,
} from 'view/atendimentos/atendimento-vacinacao/model'
import {
  getDescricaoTituloCalendario,
  getGrupoAlvoVacinacao,
} from 'view/atendimentos/atendimento-vacinacao/vacinacaoUtils'
import { validateFinalizarAtendimento } from 'view/atendimentos/atendimento-vacinacao/validator'
import { agendarConsultaValidator } from 'view/atendimentos/escuta-inicial/validator'
import { AgendamentoDia } from 'view/atendimentos/types/AgendamentoDia'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { downloadAtendimentoVacinacao } from '../components/vacinacao/downloadAtendimentoVacinacao'
import { AsideView } from '../soap/aside/AsideView'
import { AgendarConsultaPanel } from '../soap/desfecho/components/AgendarConsultaPanel'
import { DesfechoAtendimentoPanel } from '../soap/desfecho/components/DesfechoAtendimentoPanel'
import { DeclaracaoComparecimentoButton } from '../soap/plano/atestados/DeclaracaoComparecimentoBox'
import { createVacinacaoCalculator } from './calculator'
import { createVacinacaoErrorModal, VacinacaoCalendarioView } from './VacinacaoCalendarioView'

interface VacinacaoViewProps {
  atendimentoId: ID
  prontuarioId: ID
  cidadao: CidadaoAtendimento
  dataAtendimento: Instant
  agendamentosDia?: AgendamentoDia[]
  gestante?: boolean
  puerpera?: boolean
  initialValues?: AtendimentoVacinacaoState
  isAtendimentoIndividual?: boolean
  clearCache: Dispatch<void>
  updateCache?: Dispatch<AtendimentoVacinacaoState | SoapState>
  cacheState?: AtendimentoVacinacaoState | SoapState
  isUnidadeSaudeCEO?: boolean
  lastSaved?: Date
}

const atendimentoVacinacaoValidator = () =>
  createValidator<AtendimentoVacinacaoState>({
    agendarConsulta: agendarConsultaValidator,
  })

export type EstadoBotaoCalendario = 'CALENDARIO_VACINAL' | 'OUTROS_IMUNOS'

// type guard para poder acessar valores dependendo do state que foi provido no parametro
const isAtendimentoVacinacaoState = (state: any): state is AtendimentoVacinacaoState => {
  return state.registroVacinacao !== undefined
}

const disableImprimirAtendimento = (registros: RegistroVacinacaoFormModel[]) =>
  !registros.find(
    (item) => item.tipoRegistroVacinacao === TipoRegistroVacinacaoEnum.APLICACAO && item.isRegistroAnterior !== true
  )

const parseCacheAgendarConsulta = (state: AtendimentoVacinacaoState) => {
  return {
    ...state,
    agendarConsulta: {
      ...state?.agendarConsulta,
      horario: parseDateFromLocalStorage(state?.agendarConsulta?.horario),
    },
  }
}

export function VacinacaoView(props: VacinacaoViewProps) {
  const { cacheState, ...propsValues } = props

  const [initialValuesVacinacao] = useState(
    isAtendimentoVacinacaoState(cacheState)
      ? parseCacheAgendarConsulta(cacheState)
      : parseCacheAgendarConsulta(cacheState.vacinacao)
    // usado pra setar os valores iniciais do Form apenas na primeira vez que renderizar)
  )

  return (
    <div>
      <VacinacaoForm {...propsValues} initialValues={initialValuesVacinacao} />
    </div>
  )
}

const gestantePuerperaCarregadosText = (gestante: boolean, puerpera: boolean) =>
  gestante
    ? 'Possui gestação ativa na Lista de problemas / condições, registrada em atendimento anterior.'
    : puerpera
    ? 'Possui condição de puerpério na Lista de problemas / condições, registrada em atendimento anterior.'
    : undefined

const tipoAtendimento = (isAtendimentoVacinacao: boolean) => (isAtendimentoVacinacao ? 'de vacinação' : 'individual')

export const VacinacaoForm = (props: VacinacaoViewProps) => {
  const {
    prontuarioId,
    initialValues,
    cidadao,
    dataAtendimento,
    agendamentosDia,
    atendimentoId,
    clearCache,
    updateCache,
    gestante,
    puerpera,
    isUnidadeSaudeCEO,
    lastSaved,
  } = props

  const [estadoBotaoCalendario, setEstadoBotaoCalendario] = useState<EstadoBotaoCalendario>('CALENDARIO_VACINAL')
  //const isAtendimentoVacinacao = isAtendimentoVacinacaoState(cacheState)
  const isAtendimentoVacinacao = true
  const [cancelar] = useCancelarAtendimentoMutation()
  const [finalizar] = useFinalizarAtendimentoVacinacaoMutation()
  const [finalizarIndividual] = useFinalizarAtendimentoIndividualMutation()

  const isCEO = isUnidadeSaudeCEO && !!CEO_ENABLED
  const apollo = useApolloClient()

  const handleRejection = useErrorHandler()

  const grupoAlvoCidadao = getGrupoAlvoVacinacao(cidadao.dataNascimento, dataAtendimento)
  const isSexoFeminino = sexoByIdentidadeGenero(cidadao.sexo, cidadao.identidadeGeneroDbEnum) === 'FEMININO'
  const gestantePuerperaCarregados = useMemo(() => gestantePuerperaCarregadosText(gestante, puerpera), [
    gestante,
    puerpera,
  ])

  const tipoAtendimentoText = useMemo(() => tipoAtendimento(isAtendimentoVacinacao), [isAtendimentoVacinacao])

  const [mostrarCalendarioCompleto, setMostrarCalendarioCompleto] = useState<boolean>(false)

  const theme = useTheme()
  const style = useMemo(() => styles(theme), [theme])

  const history = useHistory()
  const alert = useAlert()

  const [formInitialized, setFormInitialized] = useState(false)
  useEffect(() => {
    setFormInitialized(true)
  }, [])

  const printItem = useCallback(
    (itemToPrint: ImpressaoAtendimentoVacinacaoInput) => downloadAtendimentoVacinacao(itemToPrint),
    []
  )

  const calculators = useMemo(() => createVacinacaoCalculator(apollo, isCEO), [apollo, isCEO])

  const handleFormChange = useCallback(
    (values: AtendimentoVacinacaoState) => {
      /* Avoids the error of updating a component while rendering a different component. */

      // TODO (NP) rever relacoes com o atendimento individual
      if (formInitialized) {
        //  if (isAtendimentoVacinacaoState(cacheState)) {
        values.lastSaved = new Date()
        updateCache(values)

        //  } else {
        //    cacheState.lastSaved = new Date()
        //    cacheState.vacinacao = values
        //   updateCache(cacheState)
        // }
      }
    },
    //},
    [formInitialized, updateCache]
  )
  const { analytics } = useFirebase()

  const handleCancelar = useCallback(() => {
    confirm({
      title: `Deseja cancelar o atendimento ${tipoAtendimentoText}?`,
      body: 'As alterações realizadas serão perdidas.',
      confirmLabel: 'Sim',
      cancelLabel: 'Não',
      onConfirm: () => {
        cancelar({ variables: { atendimentoId } })
          .then(() => {
            clearCache()
            history.push('/lista-atendimento')
            analytics.logEvent('cancelar_atendimento', { tipo_de_atendimento: 'vacinação' })
          })
          .catch(handleRejection)
      },
    })()
  }, [analytics, tipoAtendimentoText, cancelar, atendimentoId, handleRejection, clearCache, history])

  const handleFinalizar = useCallback(
    (values: AtendimentoVacinacaoState) => {
      // TODO verificar a possibilidade de externalizar esta funcao para nao ficar duplicada com a de atendimento individual
      if (!isAtendimentoVacinacao) {
        return finalizarIndividual({
          variables: {
            input: converAtendimentoIndividualModelToInput(atendimentoId, cidadao.id, values),
          },
        }).then((response) => {
          values.imprimirAtendimento &&
            printItem({ atendimentoProfissionalId: response.data.finalizarAtendimentoIndividual.id })
          alert('success', `Atendimento individual finalizado com sucesso.`)
          analytics.logEvent('finalizar_atendimento', { tipo_de_atendimento: 'vacinação' })
          history.push('/lista-atendimento')
          clearCache()
        })
      } else {
        const error = validateFinalizarAtendimento({ registrosAplicadosAtendimento: values.registroVacinacao })

        if (error.hasError) {
          createVacinacaoErrorModal(error)
        } else {
          return finalizar({
            variables: {
              input: convertAtendimentoVacinacaoModelToInput(atendimentoId, cidadao.id, values),
            },
          }).then((response) => {
            values.imprimirAtendimento &&
              printItem({
                atendimentoProfissionalId: response.data.finalizarAtendimentoVacinacao.atendimentoProfissional.id,
              })
            alert('success', `Atendimento de vacinação finalizado com sucesso.`)
            history.push('/lista-atendimento')
            clearCache()
          })
        }
      }
    },
    [
      analytics,
      alert,
      atendimentoId,
      cidadao.id,
      clearCache,
      finalizar,
      finalizarIndividual,
      history,
      isAtendimentoVacinacao,
      printItem,
    ]
  )

  const renderVacinacao = useCallback(
    (formProps: FormRenderProps<AtendimentoVacinacaoState>) => {
      const { handleSubmit, values } = formProps
      const disableImpressao = disableImprimirAtendimento(values.registroVacinacao)

      return (
        <Fragment>
          <div
            css={css`
              grid-area: 'main';
              padding: 0 1rem 1rem;
            `}
          >
            <FormValueSpy<AtendimentoVacinacaoState> onChange={handleFormChange} />
            <HeadingSection level={2} title='Condições' style={style.headingSection}>
              {gestantePuerperaCarregados && (
                <Alert inline style={{ marginBottom: '1rem' }} type='info'>
                  {gestantePuerperaCarregados}
                </Alert>
              )}

              <div css={isSexoFeminino && style.box}>
                <HFlow>
                  {isSexoFeminino && (
                    <Fragment>
                      <CheckboxField name={meta.condicoesVacinacao.gestante} label='Gestante' />
                      <CheckboxField label='Puérpera' name={meta.condicoesVacinacao.puerpera} style={style.separator} />
                    </Fragment>
                  )}
                  <CheckboxField name={meta.condicoesVacinacao.viajante} label='Viajante' />
                </HFlow>
              </div>
            </HeadingSection>
            <HeadingSection level={2} title='Vacinação'>
              <VFlow>
                <ButtonGroup>
                  <Button
                    kind={estadoBotaoCalendario === 'CALENDARIO_VACINAL' ? 'primary' : 'normal'}
                    size='small'
                    skin='default'
                    onClick={() => setEstadoBotaoCalendario('CALENDARIO_VACINAL')}
                  >
                    {mostrarCalendarioCompleto
                      ? 'Calendário vacinal completo'
                      : 'Calendário vacinal' +
                        getDescricaoTituloCalendario(grupoAlvoCidadao) +
                        (formProps.values.condicoesVacinacao.gestante ? ' / gestante' : '')}
                  </Button>
                  <Button
                    kind={estadoBotaoCalendario === 'OUTROS_IMUNOS' ? 'primary' : 'normal'}
                    size='small'
                    skin='default'
                    onClick={() => setEstadoBotaoCalendario('OUTROS_IMUNOS')}
                  >
                    Outros imunobiológicos
                  </Button>
                </ButtonGroup>
                <VacinacaoCalendarioView
                  cidadao={cidadao}
                  condicoesVacinacao={formProps.values.condicoesVacinacao}
                  dataAtendimento={dataAtendimento}
                  estadoBotaoCalendario={estadoBotaoCalendario}
                  grupoAlvoCidadao={grupoAlvoCidadao}
                  prontuarioId={prontuarioId}
                  registros={formProps.values.registroVacinacao}
                  formVacinacao={formProps.form}
                  mostrarCalendarioCompleto={mostrarCalendarioCompleto}
                  setMostrarCalendarioCompleto={setMostrarCalendarioCompleto}
                />
                {isAtendimentoVacinacao && (
                  <Fragment>
                    <div css={style.desfechoContainer}>
                      <Text style={style.desfechoInitial}>D</Text>
                      <Heading level={2} style={style.desfechoTitle}>
                        Dados do desfecho
                      </Heading>
                    </div>
                    <VFlow
                      style={css`
                        padding-top: 1rem;
                      `}
                    >
                      <DeclaracaoComparecimentoButton atendimentoId={atendimentoId} />
                      <DesfechoAtendimentoPanel
                        name={meta.desfechoVacinacao.desfechoAtendimento}
                        agendamentosDia={agendamentosDia}
                      />
                      <AgendarConsultaPanel
                        name={meta.agendarConsulta}
                        hasPainelLateral={ATENDIMENTO_NOVO_ENABLED ? true : false}
                      />
                    </VFlow>
                  </Fragment>
                )}
              </VFlow>
            </HeadingSection>
          </div>
          <div css={style.actions}>
            <div css={style.sideBar}></div>
            <HFlow justifyContent='space-between'>
              <Alert inline type='success' styles={{ wrapper: style.draftAlertWrapper }}>
                Rascunho salvo automaticamente às <DateTime format='HH:mm' value={lastSaved} />
              </Alert>

              <HFlow>
                <Tooltip
                  text={disableImpressao && 'Para habilitar a opção de impressão, realize um registro de aplicação.'}
                >
                  <span>
                    <CheckboxField
                      name={meta.imprimirAtendimento}
                      label='Imprimir atendimento ao finalizar'
                      style={style.print}
                      disabled={disableImpressao}
                    />
                  </span>
                </Tooltip>

                <Button kind='normal' onClick={handleCancelar}>
                  Cancelar atendimento {tipoAtendimentoText}
                </Button>
                <Button kind='primary' onClick={handleSubmit}>
                  Finalizar atendimento {tipoAtendimentoText}
                </Button>
              </HFlow>
            </HFlow>
          </div>
        </Fragment>
      )
    },
    [
      handleFormChange,
      style.headingSection,
      style.box,
      style.separator,
      style.desfechoContainer,
      style.desfechoInitial,
      style.desfechoTitle,
      style.actions,
      style.sideBar,
      style.draftAlertWrapper,
      style.print,
      gestantePuerperaCarregados,
      isSexoFeminino,
      estadoBotaoCalendario,
      mostrarCalendarioCompleto,
      grupoAlvoCidadao,
      cidadao,
      dataAtendimento,
      prontuarioId,
      isAtendimentoVacinacao,
      atendimentoId,
      agendamentosDia,
      lastSaved,
      handleCancelar,
      tipoAtendimentoText,
    ]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 60vh;
      `}
    >
      <PageContent fluid type='filled'>
        {ATENDIMENTO_NOVO_ENABLED ? (
          <div css={style.container}>
            <div css={style.aside}>
              <AsideView prontuarioId={prontuarioId} dataAtendimento={dataAtendimento} cidadaoId={cidadao.id} />
            </div>
            <Form<AtendimentoVacinacaoState>
              render={renderVacinacao}
              onSubmit={handleFinalizar}
              decorators={[calculators]}
              initialValues={initialValues}
              validate={atendimentoVacinacaoValidator()}
            />
          </div>
        ) : (
          <Form<AtendimentoVacinacaoState>
            render={renderVacinacao}
            onSubmit={handleFinalizar}
            decorators={[calculators]}
            initialValues={initialValues}
            validate={atendimentoVacinacaoValidator()}
          />
        )}
      </PageContent>
    </div>
  )
}

const styles = (theme: Theme) => {
  return {
    container: css`
      display: grid;
      grid-template-columns: 0.4fr 1fr;
      grid-template-rows: auto;
      grid-template-areas: 'sidebar main' 'footer footer';
    `,

    aside: css`
      width: 100%;
      max-width: 27rem;
    `,

    print: css`
      border: 0.063rem solid ${theme.pallete.gray.c80};
      border-radius: 0.125rem;
      padding: 0.75rem 1rem;
    `,

    actions: css`
      height: 5.125rem;
      position: relative;
      grid-area: footer;
      padding-top: 1rem;
      margin-bottom: 1rem;
    `,

    draftAlertWrapper: css`
      background-color: transparent;
      border-width: 0;
    `,

    headingDesfecho: css`
      padding: 0.5rem 0 0.5rem 0;
      background-color: ${theme.pallete.gray.c90};
    `,

    separator: css`
      border-right: 1px solid ${theme.pallete.divider};
      padding-right: 1rem;
    `,

    box: css`
      padding: 1rem;
      width: 22rem;
      border: 1px solid ${theme.pallete.gray.c80};
    `,

    sideBar: css`
      width: 100vw;
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: -53vw;
      margin-right: -50vw;
      margin-bottom: 1rem;
      border-bottom: 1px solid ${theme.pallete.divider};
    `,

    desfechoContainer: css`
      margin: 1rem 0 0 0;

      display: flex;
      border-radius: 2;
      background: ${theme.pallete.gray.c90};

      &:hover {
        background-color: ${theme.pallete.gray.c80};
      }
    `,

    desfechoInitial: css`
      background: ${theme.pallete.gray.c40};
      color: ${theme.pallete.gray.c100};
      border-radius: 4px;
      padding: 0.25rem 0.75rem;
      font-size: large;
      font-weight: bold;
      line-height: 1.8;
    `,

    desfechoTitle: css`
      flex: 1;
      padding: 0.25rem 0.75rem;
    `,

    headingSection: css`
      margin-bottom: 1rem;
    `,
  }
}
